import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  InputAdornment,
  Tabs,
  Tab,
  Alert,
  AlertTitle,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  alpha,
  useTheme,
  IconButton,
  Tooltip,
  Switch,
  FormControlLabel,
  Collapse,
  Stack,
  Chip,
} from '@mui/material';
import {
  QrCode,
  ContentCopy,
  Add,
  Refresh,
  CheckCircle,
  Download,
  ExpandMore,
  ExpandLess,
  Person,
  Business,
  Info,
  Delete,
} from '@mui/icons-material';
import { api } from '@/services/api';
import toast from 'react-hot-toast';
import { QRCodeSVG } from 'qrcode.react';

interface PixKey {
  id: string;
  type: string;
  key: string;
  status: string;
}

interface InfoAdicional {
  nome: string;
  valor: string;
}

interface ChargeResponse {
  txid: string;
  pixCopiaECola?: string;
  brcode?: string;
  location?: string;
  status: string;
  revisao?: number;
  calendario?: {
    criacao: string;
    expiracao: number;
  };
  valor?: {
    original: string;
    modalidadeAlteracao?: number;
  };
  chave?: string;
  devedor?: {
    nome?: string;
    cpf?: string;
    cnpj?: string;
  };
  solicitacaoPagador?: string;
  infoAdicionais?: InfoAdicional[];
  loc?: {
    id: number;
    location: string;
    tipoCob: string;
  };
  pix?: Array<{
    endToEndId: string;
    txid: string;
    valor: string;
    horario: string;
    infoPagador?: string;
    componentesValor?: any;
  }>;
}

const PixQRCodePage: React.FC = () => {
  const theme = useTheme();
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingKeys, setLoadingKeys] = useState(true);
  const [keys, setKeys] = useState<PixKey[]>([]);
  const [selectedKey, setSelectedKey] = useState('');

  // Charge form
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [expiracao, setExpiracao] = useState('3600');
  const [modalidadeAlteracao, setModalidadeAlteracao] = useState(false);

  // Devedor (optional)
  const [showDevedor, setShowDevedor] = useState(false);
  const [devedorNome, setDevedorNome] = useState('');
  const [devedorDocType, setDevedorDocType] = useState<'cpf' | 'cnpj'>('cpf');
  const [devedorDoc, setDevedorDoc] = useState('');

  // Info Adicionais (optional)
  const [showInfoAdicionais, setShowInfoAdicionais] = useState(false);
  const [infoAdicionais, setInfoAdicionais] = useState<InfoAdicional[]>([]);
  const [newInfoNome, setNewInfoNome] = useState('');
  const [newInfoValor, setNewInfoValor] = useState('');

  // Result
  const [generatedCharge, setGeneratedCharge] = useState<ChargeResponse | null>(null);
  const [copied, setCopied] = useState(false);
  const [paymentDetected, setPaymentDetected] = useState(false);
  const [polling, setPolling] = useState(false);

  useEffect(() => {
    loadKeys();
  }, []);

  // Polling para detectar pagamento
  useEffect(() => {
    if (!generatedCharge?.txid || paymentDetected) return;
    if (generatedCharge.status === 'CONCLUIDA' || generatedCharge.status === 'REMOVIDA_PELO_USUARIO_RECEBEDOR') return;

    setPolling(true);
    const interval = setInterval(async () => {
      try {
        const response = await api.get(`/pix/charges/${generatedCharge.txid}`);
        if (response?.success && response.data) {
          const newStatus = response.data.status;
          if (newStatus !== generatedCharge.status) {
            setGeneratedCharge(prev => prev ? { ...prev, status: newStatus, pix: response.data.pix } : prev);
          }
          if (newStatus === 'CONCLUIDA') {
            setPaymentDetected(true);
            setPolling(false);
            toast.success('Pagamento recebido!', { duration: 8000, icon: '💰' });
          }
        }
      } catch (err) {
        console.error('Polling error:', err);
      }
    }, 5000);

    return () => {
      clearInterval(interval);
      setPolling(false);
    };
  }, [generatedCharge?.txid, generatedCharge?.status, paymentDetected]);

  const loadKeys = async () => {
    setLoadingKeys(true);
    try {
      const response = await api.get('/pix/keys');
      if (response?.success) {
        setKeys(response.data || []);
        if (response.data?.length > 0) {
          setSelectedKey(response.data[0].key);
        }
      }
    } catch (err) {
      console.error('Error loading keys:', err);
    } finally {
      setLoadingKeys(false);
    }
  };

  const handleAddInfoAdicional = () => {
    if (newInfoNome.trim() && newInfoValor.trim()) {
      if (infoAdicionais.length >= 50) {
        toast.error('Máximo de 50 informações adicionais');
        return;
      }
      setInfoAdicionais([...infoAdicionais, { nome: newInfoNome.trim(), valor: newInfoValor.trim() }]);
      setNewInfoNome('');
      setNewInfoValor('');
    }
  };

  const handleRemoveInfoAdicional = (index: number) => {
    setInfoAdicionais(infoAdicionais.filter((_, i) => i !== index));
  };

  const handleGenerateQRCode = async () => {
    if (!selectedKey) {
      toast.error('Selecione uma chave PIX');
      return;
    }

    if (!amount || parseFloat(amount) <= 0) {
      toast.error('Informe um valor válido');
      return;
    }

    setLoading(true);
    setGeneratedCharge(null);

    try {
      const chargeData: any = {
        calendario: {
          expiracao: parseInt(expiracao),
        },
        valor: {
          original: parseFloat(amount).toFixed(2),
        },
        chave: selectedKey,
        solicitacaoPagador: description || 'Cobrança PIX',
      };

      // Modalidade de alteração (permite pagador alterar valor)
      if (modalidadeAlteracao) {
        chargeData.valor.modalidadeAlteracao = 1;
      }

      // Devedor (opcional)
      if (showDevedor && devedorNome) {
        chargeData.devedor = {
          nome: devedorNome,
        };
        if (devedorDoc) {
          if (devedorDocType === 'cpf') {
            chargeData.devedor.cpf = devedorDoc.replace(/\D/g, '');
          } else {
            chargeData.devedor.cnpj = devedorDoc.replace(/\D/g, '');
          }
        }
      }

      // Informações adicionais
      if (infoAdicionais.length > 0) {
        chargeData.infoAdicionais = infoAdicionais;
      }

      const response = await api.post('/pix/charges', chargeData);

      if (response?.success) {
        setGeneratedCharge(response.data);
        toast.success('Cobrança criada com sucesso!');
      } else {
        toast.error(response?.error?.message || 'Erro ao gerar cobrança');
      }
    } catch (err: any) {
      console.error('Error generating charge:', err);
      toast.error(err.message || 'Erro ao gerar QR Code');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyPixCode = () => {
    const code = generatedCharge?.pixCopiaECola || generatedCharge?.brcode;
    if (code) {
      navigator.clipboard.writeText(code);
      setCopied(true);
      toast.success('Código PIX copiado!');
      setTimeout(() => setCopied(false), 3000);
    }
  };

  const handleDownloadQRCode = () => {
    const svg = document.getElementById('qr-code-svg');
    if (!svg) return;

    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx?.drawImage(img, 0, 0);
      const pngFile = canvas.toDataURL('image/png');

      const downloadLink = document.createElement('a');
      downloadLink.download = `qrcode-${generatedCharge?.txid}.png`;
      downloadLink.href = pngFile;
      downloadLink.click();
    };

    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  const handleNewCharge = () => {
    setGeneratedCharge(null);
    setPaymentDetected(false);
    setPolling(false);
    setAmount('');
    setDescription('');
    setDevedorNome('');
    setDevedorDoc('');
    setInfoAdicionais([]);
    setShowDevedor(false);
    setShowInfoAdicionais(false);
    setModalidadeAlteracao(false);
  };

  const formatCurrency = (value: string) => {
    const num = parseFloat(value);
    return num.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };

  const formatExpiracao = (segundos: number) => {
    if (segundos < 60) return `${segundos} segundos`;
    if (segundos < 3600) return `${Math.floor(segundos / 60)} minutos`;
    if (segundos < 86400) return `${Math.floor(segundos / 3600)} horas`;
    return `${Math.floor(segundos / 86400)} dias`;
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleString('pt-BR');
    } catch {
      return dateString;
    }
  };

  const pixCode = generatedCharge?.pixCopiaECola || generatedCharge?.brcode;

  return (
    <Box>
      <Typography variant="h5" fontWeight="bold" sx={{ mb: 3 }}>
        <QrCode sx={{ mr: 1, verticalAlign: 'middle' }} />
        QR Code PIX - Cobrança Imediata
      </Typography>

      <Paper sx={{ p: 3 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
          <Tab label="Gerar Cobrança" />
          <Tab label="Ler QR Code" disabled />
        </Tabs>

        {tab === 0 && (
          <>
            {generatedCharge ? (
              // Show generated QR Code
              <Box>
                {paymentDetected ? (
                  <Alert
                    severity="success"
                    icon={<CheckCircle />}
                    sx={{ mb: 3, border: '2px solid', borderColor: 'success.main' }}
                  >
                    <AlertTitle>Pagamento Recebido!</AlertTitle>
                    {generatedCharge.pix?.[0] && (
                      <>
                        Valor: {formatCurrency(generatedCharge.pix[0].valor)} |
                        Horário: {formatDate(generatedCharge.pix[0].horario)}
                        {generatedCharge.pix[0].infoPagador && ` | Pagador: ${generatedCharge.pix[0].infoPagador}`}
                      </>
                    )}
                  </Alert>
                ) : (
                  <Alert
                    severity="info"
                    icon={polling ? <CircularProgress size={20} /> : <CheckCircle />}
                    sx={{ mb: 3 }}
                  >
                    <AlertTitle>Cobrança criada - Aguardando pagamento...</AlertTitle>
                    TXID: {generatedCharge.txid}
                    {polling && <Typography variant="caption" display="block">Verificando a cada 5 segundos...</Typography>}
                  </Alert>
                )}

                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Paper
                      sx={{
                        p: 3,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        backgroundColor: '#fff',
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                      }}
                    >
                      <Box
                        sx={{
                          p: 2,
                          backgroundColor: '#fff',
                          borderRadius: 1,
                          mb: 2,
                        }}
                      >
                        {pixCode && (
                          <QRCodeSVG
                            id="qr-code-svg"
                            value={pixCode}
                            size={220}
                            level="M"
                            includeMargin
                          />
                        )}
                      </Box>
                      <Typography variant="h5" fontWeight="bold" color="primary" sx={{ mb: 1 }}>
                        {formatCurrency(generatedCharge.valor?.original || amount)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Expira em: {formatExpiracao(generatedCharge.calendario?.expiracao || parseInt(expiracao))}
                      </Typography>

                      <Stack direction="row" spacing={1} sx={{ width: '100%', mb: 2 }}>
                        <Button
                          variant="contained"
                          startIcon={copied ? <CheckCircle /> : <ContentCopy />}
                          onClick={handleCopyPixCode}
                          color={copied ? 'success' : 'primary'}
                          fullWidth
                        >
                          {copied ? 'Copiado!' : 'Copiar Código'}
                        </Button>
                        <Button
                          variant="outlined"
                          startIcon={<Download />}
                          onClick={handleDownloadQRCode}
                          fullWidth
                        >
                          Baixar QR
                        </Button>
                      </Stack>

                      <Divider sx={{ width: '100%', my: 1 }} />

                      <Typography variant="caption" color="text.secondary">
                        Código Copia e Cola
                      </Typography>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 1,
                          mt: 1,
                          width: '100%',
                          maxHeight: 80,
                          overflow: 'auto',
                          backgroundColor: alpha(theme.palette.grey[500], 0.08),
                        }}
                      >
                        <Typography variant="caption" fontFamily="monospace" sx={{ wordBreak: 'break-all' }}>
                          {pixCode}
                        </Typography>
                      </Paper>
                    </Paper>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3 }}>
                      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                        Detalhes da Cobrança
                      </Typography>
                      <Stack spacing={1.5}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">TXID</Typography>
                          <Typography variant="body2" fontFamily="monospace" sx={{ wordBreak: 'break-all' }}>
                            {generatedCharge.txid}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Status</Typography>
                          <Box sx={{ mt: 0.5 }}>
                            <Chip
                              label={generatedCharge.status === 'CONCLUIDA' ? 'PAGO' : generatedCharge.status}
                              color={generatedCharge.status === 'CONCLUIDA' ? 'success' : 'info'}
                              size="small"
                              icon={generatedCharge.status === 'CONCLUIDA' ? <CheckCircle /> : undefined}
                            />
                          </Box>
                        </Box>
                        {generatedCharge.pix?.[0] && (
                          <Box>
                            <Typography variant="caption" color="text.secondary">Pagamento</Typography>
                            <Typography variant="body2">
                              {formatCurrency(generatedCharge.pix[0].valor)} em {formatDate(generatedCharge.pix[0].horario)}
                            </Typography>
                            <Typography variant="caption" fontFamily="monospace" color="text.secondary">
                              E2E: {generatedCharge.pix[0].endToEndId}
                            </Typography>
                          </Box>
                        )}
                        <Box>
                          <Typography variant="caption" color="text.secondary">Valor</Typography>
                          <Typography variant="body2">
                            {formatCurrency(generatedCharge.valor?.original || amount)}
                            {generatedCharge.valor?.modalidadeAlteracao === 1 && (
                              <Chip label="Valor alterável" size="small" sx={{ ml: 1 }} />
                            )}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Chave PIX</Typography>
                          <Typography variant="body2" fontFamily="monospace">{generatedCharge.chave || selectedKey}</Typography>
                        </Box>
                        {generatedCharge.solicitacaoPagador && (
                          <Box>
                            <Typography variant="caption" color="text.secondary">Descrição</Typography>
                            <Typography variant="body2">{generatedCharge.solicitacaoPagador}</Typography>
                          </Box>
                        )}
                        {generatedCharge.devedor && (
                          <Box>
                            <Typography variant="caption" color="text.secondary">Devedor</Typography>
                            <Typography variant="body2">
                              {generatedCharge.devedor.nome}
                              {generatedCharge.devedor.cpf && ` (CPF: ***${generatedCharge.devedor.cpf.slice(-4)})`}
                              {generatedCharge.devedor.cnpj && ` (CNPJ: ***${generatedCharge.devedor.cnpj.slice(-4)})`}
                            </Typography>
                          </Box>
                        )}
                        {generatedCharge.calendario?.criacao && (
                          <Box>
                            <Typography variant="caption" color="text.secondary">Criação</Typography>
                            <Typography variant="body2">{formatDate(generatedCharge.calendario.criacao)}</Typography>
                          </Box>
                        )}
                        {generatedCharge.location && (
                          <Box>
                            <Typography variant="caption" color="text.secondary">Location</Typography>
                            <Typography variant="body2" fontFamily="monospace" sx={{ wordBreak: 'break-all', fontSize: '0.75rem' }}>
                              {generatedCharge.location}
                            </Typography>
                          </Box>
                        )}
                        {generatedCharge.infoAdicionais && generatedCharge.infoAdicionais.length > 0 && (
                          <Box>
                            <Typography variant="caption" color="text.secondary">Informações Adicionais</Typography>
                            {generatedCharge.infoAdicionais.map((info, idx) => (
                              <Typography key={idx} variant="body2">
                                {info.nome}: {info.valor}
                              </Typography>
                            ))}
                          </Box>
                        )}
                      </Stack>

                      <Divider sx={{ my: 2 }} />

                      <Button
                        variant="outlined"
                        startIcon={<Add />}
                        onClick={handleNewCharge}
                        fullWidth
                      >
                        Nova Cobrança
                      </Button>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>
            ) : (
              // Show form to create new charge
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Stack spacing={2}>
                    {/* Chave PIX */}
                    <FormControl fullWidth>
                      <InputLabel>Chave PIX para Recebimento *</InputLabel>
                      <Select
                        value={selectedKey}
                        label="Chave PIX para Recebimento *"
                        onChange={(e) => setSelectedKey(e.target.value)}
                        disabled={loadingKeys}
                      >
                        {keys.map((key) => (
                          <MenuItem key={key.id} value={key.key}>
                            {key.type}: {key.key}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    {keys.length === 0 && !loadingKeys && (
                      <Alert severity="warning">
                        Você precisa cadastrar uma chave PIX primeiro.
                      </Alert>
                    )}

                    {/* Valor */}
                    <TextField
                      fullWidth
                      label="Valor *"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                      }}
                      placeholder="0,00"
                      type="number"
                    />

                    {/* Modalidade de alteração */}
                    <FormControlLabel
                      control={
                        <Switch
                          checked={modalidadeAlteracao}
                          onChange={(e) => setModalidadeAlteracao(e.target.checked)}
                        />
                      }
                      label="Permitir pagador alterar valor"
                    />

                    {/* Descrição */}
                    <TextField
                      fullWidth
                      label="Descrição (exibida ao pagador)"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      multiline
                      rows={2}
                      placeholder="Ex: Pagamento ref. NF 12345"
                    />

                    {/* Expiração */}
                    <FormControl fullWidth>
                      <InputLabel>Expiração</InputLabel>
                      <Select
                        value={expiracao}
                        label="Expiração"
                        onChange={(e) => setExpiracao(e.target.value)}
                      >
                        <MenuItem value="900">15 minutos</MenuItem>
                        <MenuItem value="1800">30 minutos</MenuItem>
                        <MenuItem value="3600">1 hora</MenuItem>
                        <MenuItem value="7200">2 horas</MenuItem>
                        <MenuItem value="21600">6 horas</MenuItem>
                        <MenuItem value="43200">12 horas</MenuItem>
                        <MenuItem value="86400">24 horas</MenuItem>
                        <MenuItem value="172800">2 dias</MenuItem>
                        <MenuItem value="604800">7 dias</MenuItem>
                      </Select>
                    </FormControl>

                    <Divider />

                    {/* Devedor (opcional) */}
                    <Box>
                      <Button
                        variant="text"
                        startIcon={<Person />}
                        endIcon={showDevedor ? <ExpandLess /> : <ExpandMore />}
                        onClick={() => setShowDevedor(!showDevedor)}
                        sx={{ mb: 1 }}
                      >
                        Dados do Pagador (opcional)
                      </Button>
                      <Collapse in={showDevedor}>
                        <Stack spacing={2} sx={{ pl: 2 }}>
                          <TextField
                            fullWidth
                            label="Nome do Pagador"
                            value={devedorNome}
                            onChange={(e) => setDevedorNome(e.target.value)}
                            size="small"
                          />
                          <Stack direction="row" spacing={1}>
                            <FormControl size="small" sx={{ minWidth: 100 }}>
                              <InputLabel>Tipo</InputLabel>
                              <Select
                                value={devedorDocType}
                                label="Tipo"
                                onChange={(e) => setDevedorDocType(e.target.value as 'cpf' | 'cnpj')}
                              >
                                <MenuItem value="cpf">CPF</MenuItem>
                                <MenuItem value="cnpj">CNPJ</MenuItem>
                              </Select>
                            </FormControl>
                            <TextField
                              fullWidth
                              label={devedorDocType === 'cpf' ? 'CPF' : 'CNPJ'}
                              value={devedorDoc}
                              onChange={(e) => setDevedorDoc(e.target.value.replace(/\D/g, ''))}
                              size="small"
                              placeholder={devedorDocType === 'cpf' ? '00000000000' : '00000000000000'}
                              inputProps={{ maxLength: devedorDocType === 'cpf' ? 11 : 14 }}
                            />
                          </Stack>
                        </Stack>
                      </Collapse>
                    </Box>

                    {/* Informações Adicionais (opcional) */}
                    <Box>
                      <Button
                        variant="text"
                        startIcon={<Info />}
                        endIcon={showInfoAdicionais ? <ExpandLess /> : <ExpandMore />}
                        onClick={() => setShowInfoAdicionais(!showInfoAdicionais)}
                        sx={{ mb: 1 }}
                      >
                        Informações Adicionais (opcional)
                      </Button>
                      <Collapse in={showInfoAdicionais}>
                        <Stack spacing={2} sx={{ pl: 2 }}>
                          {infoAdicionais.map((info, idx) => (
                            <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Chip
                                label={`${info.nome}: ${info.valor}`}
                                onDelete={() => handleRemoveInfoAdicional(idx)}
                                size="small"
                              />
                            </Box>
                          ))}
                          <Stack direction="row" spacing={1} alignItems="flex-end">
                            <TextField
                              label="Nome"
                              value={newInfoNome}
                              onChange={(e) => setNewInfoNome(e.target.value)}
                              size="small"
                              sx={{ flex: 1 }}
                              inputProps={{ maxLength: 50 }}
                            />
                            <TextField
                              label="Valor"
                              value={newInfoValor}
                              onChange={(e) => setNewInfoValor(e.target.value)}
                              size="small"
                              sx={{ flex: 1 }}
                              inputProps={{ maxLength: 200 }}
                            />
                            <IconButton
                              color="primary"
                              onClick={handleAddInfoAdicional}
                              disabled={!newInfoNome.trim() || !newInfoValor.trim()}
                            >
                              <Add />
                            </IconButton>
                          </Stack>
                          <Typography variant="caption" color="text.secondary">
                            Máximo de 50 informações adicionais. Nome até 50 caracteres, valor até 200.
                          </Typography>
                        </Stack>
                      </Collapse>
                    </Box>

                    <Divider />

                    {/* Submit */}
                    <Button
                      variant="contained"
                      fullWidth
                      size="large"
                      startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <QrCode />}
                      onClick={handleGenerateQRCode}
                      disabled={loading || !selectedKey || !amount}
                    >
                      {loading ? 'Gerando...' : 'Gerar QR Code PIX'}
                    </Button>
                  </Stack>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Paper
                    sx={{
                      p: 3,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      backgroundColor: alpha(theme.palette.grey[500], 0.05),
                      minHeight: 400,
                      justifyContent: 'center',
                    }}
                  >
                    <Box
                      sx={{
                        width: 220,
                        height: 220,
                        backgroundColor: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: `2px dashed ${alpha(theme.palette.grey[500], 0.3)}`,
                        borderRadius: 2,
                        mb: 2,
                      }}
                    >
                      <QrCode sx={{ fontSize: 100, color: alpha(theme.palette.grey[500], 0.3) }} />
                    </Box>
                    <Typography color="text.secondary" textAlign="center">
                      Preencha os dados ao lado e clique em "Gerar QR Code PIX"
                    </Typography>

                    <Divider sx={{ my: 3, width: '100%' }} />

                    <Alert severity="info" sx={{ width: '100%' }}>
                      <AlertTitle>API Sicoob - Cobrança Imediata</AlertTitle>
                      <Typography variant="body2">
                        Esta cobrança utiliza o endpoint <code>POST /cob</code> da API PIX do Sicoob.
                        O QR Code gerado é dinâmico e pode ser pago apenas uma vez.
                      </Typography>
                    </Alert>
                  </Paper>
                </Grid>
              </Grid>
            )}
          </>
        )}

        {tab === 1 && (
          <Box sx={{ textAlign: 'center', py: 5 }}>
            <QrCode sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
            <Typography color="text.secondary">
              Funcionalidade de leitura de QR Code em desenvolvimento
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default PixQRCodePage;
